<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col lg="2" md="2" cols="12">
                      <v-date-input
                        label="From Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="from_date.value.value"
                        :error-messages="from_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-date-input
                        label="To Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="to_date.value.value"
                        :error-messages="to_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn
                        class="bg-brand w-15"
                        type="submit"
                        ref="searchButton"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid style="margin-top: -20px">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Buyer Order Records
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                        >
                          CSV
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                        >
                          EXCEL
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                        >
                          PDF
                        </v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="goToCreate"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi-plus"
                        rounded="xl"
                      >
                        Add Buyer Order
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-row class="pt-0">
                  <v-col cols="12">
                    <div class="scrollable-table-wrapper">
                      <v-data-table
                        :headers="headers"
                        :items="buyerOrderList"
                        :loading="loading"
                        v-model:items-per-page="itemsPerPage"
                        :search="search"
                        :hover="true"
                        density="compact"
                        class="responsive-table"
                      >
                        <template v-slot:item.index="{ index }">
                          {{ ++index }}
                        </template>

                        <template v-slot:item.code="{ item }">
                          {{ item.code }}
                        </template>

                        <template v-slot:item.buyer="{ item }">
                          {{ item.buyer?.company_name || "-" }}
                        </template>

                        <template v-slot:item.order_date="{ item }">
                          {{ formatDate(item.order_date) }}
                        </template>

                        <template v-slot:item.order_receive_date="{ item }">
                          {{ item.order_receive_date ? formatDate(item.order_receive_date) : "-" }}
                        </template>

                        <template v-slot:item.delivery_date="{ item }">
                          {{ formatDate(item.delivery_date) }}
                        </template>

                        <template v-slot:item.shipment_date="{ item }">
                          {{ item.shipment_date ? formatDate(item.shipment_date) : "-" }}
                        </template>

                        <template v-slot:item.total_items="{ item }">
                          {{ item.items ? item.items.length : 0 }}
                        </template>

                        <template v-slot:item.status="{ item }">
                          <v-chip
                            size="small"
                            :color="item.status == 1 ? 'success' : 'grey'"
                            variant="tonal"
                          >
                            {{ item.status == 1 ? "Final" : "Draft" }}
                          </v-chip>
                        </template>

                        <template v-slot:item.actions="{ item }">
                          <div class="d-flex align-center justify-center" style="gap: 6px;">
                            <v-tooltip text="Edit">
                              <template v-slot:activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-pencil"
                                  size="small"
                                  variant="text"
                                  color="primary"
                                  @click="goToEdit(item.id)"
                                  v-if="$updateAccess($route.fullPath)"
                                ></v-btn>
                              </template>
                            </v-tooltip>

                            <v-tooltip text="View">
                              <template v-slot:activator="{ props }">
                                <v-btn
                                  v-bind="props"
                                  icon="mdi-eye"
                                  size="small"
                                  variant="text"
                                  color="info"
                                  @click="viewDetails(item)"
                                ></v-btn>
                              </template>
                            </v-tooltip>
                          </div>
                        </template>

                        <!-- Expanded Row for Items and Breakdowns -->
                        <!-- <template v-slot:expanded-row="{ item }">
                          <tr>
                            <td colspan="100%">
                              <v-card flat class="pa-3 bg-grey-lighten-4">
                                <v-data-table
                                  :headers="itemHeaders"
                                  :items="item.items || []"
                                  item-key="id"
                                  density="compact"
                                  :items-per-page="(item.items || []).length"
                                  hide-default-footer  
                                  show-expand
                                >
                                  <template #item.style="{ item: rowItem }">
                                    {{ rowItem.style?.name || "-" }}
                                  </template>

                                  <template #expanded-row="{ item: rowItem }">
                                    <tr>
                                      <td colspan="100%">
                                        <v-card flat class="pa-3 bg-grey-lighten-5">
                                          <v-data-table
                                            :headers="breakdownHeaders"
                                            :items="rowItem.breakdowns || []"
                                            :items-per-page="(rowItem.breakdowns || []).length"
                                            hide-default-footer 
                                            density="compact"
                                          >
                                            <template #item.size="{ item: bd }">
                                              {{ bd.size?.name || "-" }}
                                            </template>
                                            <template #item.color="{ item: bd }">
                                              {{ bd.color?.name || "-" }}
                                            </template>
                                          </v-data-table>
                                        </v-card>
                                      </td>
                                    </tr>
                                  </template>
                                </v-data-table>
                              </v-card>
                            </td>
                          </tr>
                        </template> -->

                        <template v-slot:no-data>
                          <div class="text-center py-4">
                            <p>No buyer order records found</p>
                          </div>
                        </template>
                      </v-data-table>
                    </div>
                  </v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Buyer Order Details</span>
          <v-btn icon="mdi-close" variant="text" @click="detailsDialog = false"></v-btn>
        </v-card-title>
        <v-card-text v-if="selectedItem">
          <v-row>
            <v-col cols="12" md="6">
              <strong>Code:</strong> {{ selectedItem.code }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Buyer:</strong> {{ selectedItem.buyer?.company_name || "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Order Date:</strong> {{ formatDate(selectedItem.order_date) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Order Receive Date:</strong> {{ selectedItem.order_receive_date ? formatDate(selectedItem.order_receive_date) : "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Delivery Date:</strong> {{ formatDate(selectedItem.delivery_date) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Shipment Date:</strong> {{ selectedItem.shipment_date ? formatDate(selectedItem.shipment_date) : "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Order Type:</strong> {{ selectedItem.order_type || "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Season:</strong> {{ selectedItem.season || "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Shipment Mode:</strong> {{ selectedItem.shipment_mode || "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Delivery Terms:</strong> {{ selectedItem.delivery_terms || "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Order Priority:</strong> {{ selectedItem.order_priority || "-" }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Status:</strong> 
              <v-chip
                size="small"
                :color="selectedItem.status == 1 ? 'success' : 'grey'"
                variant="tonal"
              >
                {{ selectedItem.status == 1 ? "Final" : "Draft" }}
              </v-chip>
            </v-col>
            <v-col cols="12">
              <strong>Items:</strong>
              <v-table v-if="selectedItem.items && selectedItem.items.length > 0" density="compact">
                <thead>
                  <tr>
                    <th>Style</th>
                    <th>Order Quantity</th>
                    <th>Unit Price</th>
                    <th>Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in selectedItem.items" :key="idx">
                    <td>{{ item.style?.name || "-" }}</td>
                    <td>{{ item.order_quantity }}</td>
                    <td>{{ item.unit_price }}</td>
                    <td>{{ item.total_value }}</td>
                  </tr>
                </tbody>
              </v-table>
              <v-alert v-else type="info" variant="tonal" class="mt-2">
                No items found
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="outlined"
            @click="goToEditFromDetails"
            prepend-icon="mdi-pencil"
            v-if="$updateAccess($route.fullPath)"
          >
            Edit
          </v-btn>
          <v-btn color="primary" variant="tonal" @click="detailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { ref, onMounted } from "vue";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";

export default {
  name: "BuyerOrderList",

  data() {
    return {
      panel: [0],
      loading: false,
      search: "",
      itemsPerPage: 10,
      detailsDialog: false,
      selectedItem: null,
      headers: [
        {
          title: "#SL",
          align: "center",
          key: "index",
          width: "80px",
          sortable: false,
        },
        {
          title: "Code",
          key: "code",
          align: "left",
          sortable: true,
        },
        {
          title: "Buyer",
          key: "buyer",
          align: "left",
          sortable: false,
        },
        {
          title: "Order Date",
          key: "order_date",
          align: "left",
          sortable: true,
        },
        {
          title: "Order Receive Date",
          key: "order_receive_date",
          align: "left",
          sortable: true,
        },
        {
          title: "Delivery Date",
          key: "delivery_date",
          align: "left",
          sortable: true,
        },
        {
          title: "Shipment Date",
          key: "shipment_date",
          align: "left",
          sortable: true,
        },
        {
          title: "Order Type",
          key: "order_type",
          align: "left",
          sortable: true,
        },
        {
          title: "Items",
          key: "total_items",
          align: "center",
          sortable: false,
        },
        {
          title: "Status",
          key: "status",
          align: "center",
          sortable: true,
        },
        {
          title: "Actions",
          key: "actions",
          align: "center",
          sortable: false,
          width: "120px",
        },
        {
          title: "",
          key: "data-table-expand",
        },
      ],
      itemHeaders: [
        { title: "Style", key: "style" },
        { title: "Order Qty", key: "order_quantity" },
        { title: "Unit Price", key: "unit_price" },
        { title: "Total Value", key: "total_value" },
      ],
      breakdownHeaders: [
        { title: "Size", key: "size" },
        { title: "Color", key: "color" },
        { title: "Quantity", key: "quantity" },
        { title: "Unit Price", key: "unit_price" },
        { title: "Consumption", key: "consumption" },
      ],
    };
  },

  computed: {
    buyerOrderList() {
      return this.$store.getters.getBuyerOrderList || [];
    },
  },

  setup() {
    const store = useStore();
    const router = useRouter();

    const { handleSubmit } = useForm({
      validationSchema: yup.object({
        from_date: yup.date().nullable(),
        to_date: yup.date().nullable(),
      }),
    });

    const from_date = useField("from_date");
    const to_date = useField("to_date");

    const submit = handleSubmit((values) => {
      // Implement search logic if needed
      store.dispatch("getBuyerOrderList");
    });

    const goToCreate = () => {
      router.push({ name: "buyer-order-create" });
    };

    const goToEdit = (id) => {
      router.push({ name: "buyer-order-create", params: { id } });
    };

    const formatDate = (date) => {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString();
    };

    const viewDetails = (item) => {
      // Load full details if needed
      router.push({ name: "buyer-order-view", params: { id: item.id } });

      // store
      //   .dispatch("getBuyerOrderById", { id: item.id })
      //   .then((res) => {
      //     this.selectedItem = res.data.data;
      //     this.detailsDialog = true;
      //   })
      //   .catch(() => {
      //     toast.error("Failed to load buyer order details");
      //   });
    };

    const goToEditFromDetails = () => {
      if (this.selectedItem) {
        router.push({ name: "buyer-order-create", params: { id: this.selectedItem.id } });
        this.detailsDialog = false;
      }
    };

    const exportToCSV = () => {
      toast.info("CSV export functionality to be implemented");
    };

    const exportToExcel = () => {
      toast.info("Excel export functionality to be implemented");
    };

    const exportToPDF = () => {
      toast.info("PDF export functionality to be implemented");
    };

    onMounted(() => {
      store.dispatch("getBuyerOrderList");
    });

    return {
      from_date,
      to_date,
      submit,
      goToCreate,
      goToEdit,
      formatDate,
      viewDetails,
      goToEditFromDetails,
      exportToCSV,
      exportToExcel,
      exportToPDF,
    };
  },

  methods: {
    loadList() {
      this.loading = true;
      this.$store.dispatch("getBuyerOrderList").finally(() => {
        this.loading = false;
      });
    },
  },

  created() {
    this.loadList();
  },
};
</script>

<style scoped>
.scrollable-table-wrapper {
  overflow-x: auto;
}

.responsive-table {
  width: 100%;
}
</style>
